// Configuración del piso: estaciones, equipos (capacidad finita), horas de
// servicio por contrato, umbrales del semáforo y pantallas del andón.
// Catering → Piso de cocina → Estaciones y equipos.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Cog, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, num, toNum } from '@/features/inventario/shared';
import {
  Contrato, Equipo, Estacion, PisoPantalla, SERVICIOS, SERVICIO_LABEL, ServicioHora,
  cat, invalidateCatering,
} from './shared';

type Tab = 'estaciones' | 'equipos' | 'horas' | 'umbrales' | 'pantallas';

interface PisoConfig {
  empresa_id: string; holgura_verde_min: number; holgura_ambar_min: number;
  buffer_despacho_min: number; porciones_por_min: number;
  peligro_alarma_min: number; peligro_max_min: number;
}

export function EstacionesCatering() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('estaciones');
  const invalidar = () => invalidateCatering(qc);

  const { data: estaciones = [] } = useQuery<Estacion[]>({
    queryKey: ['cat-estaciones'],
    queryFn: () => cat.list<Estacion>('estaciones', '?select=*&deleted_at=is.null&order=orden'),
  });
  const { data: equipos = [] } = useQuery<Equipo[]>({
    queryKey: ['cat-equipos'],
    queryFn: () => cat.list<Equipo>('equipos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const { data: horas = [] } = useQuery<ServicioHora[]>({
    queryKey: ['cat-servicio-horas'],
    queryFn: () => cat.list<ServicioHora>('servicio_horas', '?select=*&deleted_at=is.null&order=hora'),
  });
  const { data: contratos = [] } = useQuery<Contrato[]>({
    queryKey: ['cat-contratos'],
    queryFn: () => cat.list<Contrato>('contratos', '?select=*&deleted_at=is.null&order=codigo'),
  });
  const { data: cfgs = [] } = useQuery<PisoConfig[]>({
    queryKey: ['cat-piso-config'],
    queryFn: () => cat.list<PisoConfig>('piso_config', '?select=*'),
  });
  const { data: pantallas = [] } = useQuery<PisoPantalla[]>({
    queryKey: ['cat-piso-pantallas'],
    queryFn: () => cat.list<PisoPantalla>('piso_pantalla', '?select=*&deleted_at=is.null&order=nombre'),
  });
  const cfg = cfgs[0];

  // ---- Estaciones ----
  const [eCodigo, setECodigo] = useState('');
  const [eNombre, setENombre] = useState('');
  const crearEstacion = useMutation({
    mutationFn: () => cat.create('estaciones', { codigo: eCodigo.toUpperCase(), nombre: eNombre, orden: (estaciones.length + 1) * 10 }),
    onSuccess: () => { toast.success('Estación creada'); setECodigo(''); setENombre(''); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  // ---- Equipos ----
  const [qCodigo, setQCodigo] = useState('');
  const [qNombre, setQNombre] = useState('');
  const [qTipo, setQTipo] = useState('marmita');
  const [qEst, setQEst] = useState('');
  const [qUnidades, setQUnidades] = useState('1');
  const [qCap, setQCap] = useState('');
  const crearEquipo = useMutation({
    mutationFn: () => cat.create('equipos', {
      codigo: qCodigo.toUpperCase(), nombre: qNombre, tipo: qTipo,
      estacion_id: qEst || null, unidades: toNum(qUnidades) || 1,
      capacidad_porciones: qCap ? toNum(qCap) : null,
    }),
    onSuccess: () => { toast.success('Equipo creado'); setQCodigo(''); setQNombre(''); setQCap(''); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  // ---- Horas de servicio ----
  const [hContrato, setHContrato] = useState('');
  const [hServicio, setHServicio] = useState('almuerzo_1');
  const [hHora, setHHora] = useState('12:00');
  const crearHora = useMutation({
    mutationFn: () => cat.create('servicio_horas', { contrato_id: hContrato || null, servicio: hServicio, hora: hHora }),
    onSuccess: () => { toast.success('Hora de servicio guardada'); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  // ---- Umbrales ----
  const [edit, setEdit] = useState<Partial<PisoConfig>>({});
  const guardarCfg = useMutation({
    mutationFn: async () => {
      const body = { ...edit };
      if (cfg) return cat.update('piso_config', cfg.empresa_id, body);
      return cat.create('piso_config', body);
    },
    onSuccess: () => { toast.success('Umbrales actualizados'); setEdit({}); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  // ---- Pantallas ----
  const [pNombre, setPNombre] = useState('');
  const [pEst, setPEst] = useState('');
  const crearPantalla = useMutation({
    mutationFn: () => cat.create('piso_pantalla', { nombre: pNombre, estacion_id: pEst || null }),
    onSuccess: () => { toast.success('Pantalla creada — copie su dirección en el Andón'); setPNombre(''); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  const borrar = useMutation({
    mutationFn: ({ tabla, id }: { tabla: string; id: string }) => cat.remove(tabla, id),
    onSuccess: () => { toast.success('Eliminado'); invalidar(); },
    onError: (er) => toast.error(getErrorMessage(er)),
  });

  const tabs: [Tab, string][] = [
    ['estaciones', 'Estaciones'], ['equipos', 'Equipos'], ['horas', 'Horas de servicio'],
    ['umbrales', 'Umbrales del semáforo'], ['pantallas', 'Pantallas del andón'],
  ];

  return (
    <div className="space-y-4 p-4">
      <h1 className="text-lg font-semibold flex items-center gap-2"><Cog className="w-5 h-5" /> Configuración del piso</h1>
      <div className="flex flex-wrap items-center gap-2 border-b">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}
          </button>
        ))}
      </div>

      {tab === 'estaciones' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Estaciones de cocina</CardTitle>
            <p className="text-sm text-muted-foreground">
              Cada estación tiene su propia secuencia de estados: bebidas y cocina fría no pasan por cocción.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Nombre</th>
                <th className="py-1 pr-2">Secuencia de estados</th><th className="py-1 pr-2 text-right">Orden</th><th />
              </tr></thead>
              <tbody>
                {estaciones.map((e) => (
                  <tr key={e.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 font-mono text-xs">{e.codigo}</td>
                    <td className="py-1.5 pr-2">{e.nombre}</td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">{(e.estados ?? []).join(' → ')}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{e.orden}</td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => borrar.mutate({ tabla: 'estaciones', id: e.id })}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
              <Campo label="Código"><input className={fieldCls} value={eCodigo} onChange={(e) => setECodigo(e.target.value)} placeholder="PARRILLA" /></Campo>
              <Campo label="Nombre"><input className={fieldCls} value={eNombre} onChange={(e) => setENombre(e.target.value)} placeholder="Parrilla" /></Campo>
              <Button size="sm" disabled={!eCodigo || !eNombre || crearEstacion.isPending} onClick={() => crearEstacion.mutate()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'equipos' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Equipos (capacidad finita)</CardTitle>
            <p className="text-sm text-muted-foreground">
              El WIP real de una cocina son las marmitas y hornos disponibles. Si hay 3 marmitas y 4 batches las
              necesitan a la misma hora, el tablero lo avisa.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Código</th><th className="py-1 pr-2">Equipo</th><th className="py-1 pr-2">Tipo</th>
                <th className="py-1 pr-2">Estación</th><th className="py-1 pr-2 text-right">Unidades</th>
                <th className="py-1 pr-2 text-right">Capacidad</th><th />
              </tr></thead>
              <tbody>
                {equipos.map((q) => (
                  <tr key={q.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2 font-mono text-xs">{q.codigo}</td>
                    <td className="py-1.5 pr-2">{q.nombre}</td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">{q.tipo}</td>
                    <td className="py-1.5 pr-2 text-xs">{estaciones.find((e) => e.id === q.estacion_id)?.nombre ?? '—'}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{q.unidades}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{q.capacidad_porciones ? num(q.capacidad_porciones, 0) : '—'}</td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => borrar.mutate({ tabla: 'equipos', id: q.id })}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="grid grid-cols-2 items-end gap-2 rounded-md border p-3 md:grid-cols-6">
              <Campo label="Código"><input className={fieldCls} value={qCodigo} onChange={(e) => setQCodigo(e.target.value)} /></Campo>
              <Campo label="Nombre"><input className={fieldCls} value={qNombre} onChange={(e) => setQNombre(e.target.value)} /></Campo>
              <Campo label="Tipo">
                <select className={fieldCls} value={qTipo} onChange={(e) => setQTipo(e.target.value)}>
                  {['marmita', 'horno', 'arrocera', 'freidora', 'plancha', 'abatidor', 'mesa', 'licuadora', 'linea'].map((x) => <option key={x}>{x}</option>)}
                </select>
              </Campo>
              <Campo label="Estación">
                <select className={fieldCls} value={qEst} onChange={(e) => setQEst(e.target.value)}>
                  <option value="">—</option>
                  {estaciones.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Unidades"><input className={fieldCls} value={qUnidades} onChange={(e) => setQUnidades(e.target.value)} /></Campo>
              <Campo label="Capacidad">
                <div className="flex gap-2">
                  <input className={fieldCls} value={qCap} onChange={(e) => setQCap(e.target.value)} placeholder="porciones" />
                  <Button size="sm" disabled={!qCodigo || !qNombre || crearEquipo.isPending} onClick={() => crearEquipo.mutate()}>
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </Campo>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'horas' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Horas de servicio</CardTitle>
            <p className="text-sm text-muted-foreground">
              De aquí sale la hora límite de cada batch. Sin contrato = valor por defecto de la empresa;
              con contrato manda el contrato (el campamento almuerza 12:00 y el hospital 11:30).
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Contrato</th><th className="py-1 pr-2">Servicio</th>
                <th className="py-1 pr-2">Hora</th><th />
              </tr></thead>
              <tbody>
                {horas.map((h) => (
                  <tr key={h.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">
                      {h.contrato_id
                        ? (contratos.find((c) => c.id === h.contrato_id)?.codigo ?? '—')
                        : <span className="text-muted-foreground">Por defecto</span>}
                    </td>
                    <td className="py-1.5 pr-2">{SERVICIO_LABEL[h.servicio] ?? h.servicio}</td>
                    <td className="py-1.5 pr-2 font-mono">{String(h.hora).slice(0, 5)}</td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => borrar.mutate({ tabla: 'servicio_horas', id: h.id })}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
              <Campo label="Contrato">
                <select className={fieldCls} value={hContrato} onChange={(e) => setHContrato(e.target.value)}>
                  <option value="">Por defecto (empresa)</option>
                  {contratos.map((c) => <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Servicio">
                <select className={fieldCls} value={hServicio} onChange={(e) => setHServicio(e.target.value)}>
                  {SERVICIOS.map((s) => <option key={s} value={s}>{SERVICIO_LABEL[s]}</option>)}
                </select>
              </Campo>
              <Campo label="Hora"><input type="time" className={fieldCls} value={hHora} onChange={(e) => setHHora(e.target.value)} /></Campo>
              <Button size="sm" disabled={crearHora.isPending} onClick={() => crearHora.mutate()}><Plus className="h-4 w-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'umbrales' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Umbrales del semáforo y del reloj sanitario</CardTitle>
            <p className="text-sm text-muted-foreground">
              El color de cada batch se calcula con la holgura contra su hora límite; el reloj sanitario cuenta
              los minutos en zona de peligro (5–60 °C) desde que el batch queda listo.
            </p>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {([
              ['holgura_verde_min', 'Verde si hay más de (min)', 30],
              ['holgura_ambar_min', 'Ámbar hasta (min)', 0],
              ['buffer_despacho_min', 'Buffer de despacho (min)', 20],
              ['porciones_por_min', 'Ritmo de porcionado (porc/min)', 60],
              ['peligro_alarma_min', 'Alarma sanitaria a los (min)', 120],
              ['peligro_max_min', 'Vencido sanitario a los (min)', 240],
            ] as [keyof PisoConfig, string, number][]).map(([k, label, def]) => (
              <Campo key={k} label={label}>
                <input className={fieldCls}
                  value={String(edit[k] ?? cfg?.[k] ?? def)}
                  onChange={(e) => setEdit((s) => ({ ...s, [k]: toNum(e.target.value) }))} />
              </Campo>
            ))}
            <div className="md:col-span-3">
              <Button disabled={!Object.keys(edit).length || guardarCfg.isPending} onClick={() => guardarCfg.mutate()}>
                Guardar umbrales
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {tab === 'pantallas' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Pantallas del andón</CardTitle>
            <p className="text-sm text-muted-foreground">
              Cada pantalla tiene su token: la TV de cocina abre la dirección y entra sin login.
              Las direcciones se copian desde el Andón.
            </p>
          </CardHeader>
          <CardContent className="space-y-3">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Nombre</th><th className="py-1 pr-2">Estación</th>
                <th className="py-1 pr-2">Último latido</th><th />
              </tr></thead>
              <tbody>
                {pantallas.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2">{p.nombre}</td>
                    <td className="py-1.5 pr-2 text-xs">{estaciones.find((e) => e.id === p.estacion_id)?.nombre ?? 'General'}</td>
                    <td className="py-1.5 pr-2 text-xs text-muted-foreground">
                      {p.last_seen ? new Date(p.last_seen).toLocaleString() : 'nunca'}
                    </td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="ghost" onClick={() => borrar.mutate({ tabla: 'piso_pantalla', id: p.id })}>
                        <Trash2 className="h-3.5 w-3.5 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex flex-wrap items-end gap-2 rounded-md border p-3">
              <Campo label="Nombre"><input className={fieldCls} value={pNombre} onChange={(e) => setPNombre(e.target.value)} placeholder="TV pasillo central" /></Campo>
              <Campo label="Estación (opcional)">
                <select className={fieldCls} value={pEst} onChange={(e) => setPEst(e.target.value)}>
                  <option value="">Andón general</option>
                  {estaciones.map((e) => <option key={e.id} value={e.id}>{e.nombre}</option>)}
                </select>
              </Campo>
              <Button size="sm" disabled={!pNombre || crearPantalla.isPending} onClick={() => crearPantalla.mutate()}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
